/**
 * Guided join: when a field is dropped from a dataset that has no relationship to the widget's
 * dataset, we offer to create it right here (with auto-detected key suggestions) and then retry
 * the drop — instead of sending the author off to the Model view.
 */
import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { KeyPairsEditor } from "@/components/bi/KeyPairsEditor";
import { Link2, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const CARDINALITY = [
  { value: "many_to_one", label: "Many to one — many left rows per right row (most common)" },
  { value: "one_to_many", label: "One to many" },
  { value: "one_to_one", label: "One to one" },
  { value: "many_to_many", label: "Many to many" },
];

export function CreateRelationshipDialog({ open, onOpenChange, leftDataset, rightDataset, onCreated }) {
  const [keys, setKeys] = useState([{ left_field: "", right_field: "" }]);
  const [name, setName] = useState("");
  const [cardinality, setCardinality] = useState("many_to_one");
  const [direction, setDirection] = useState("both");
  const [busy, setBusy] = useState(false);
  const [detecting, setDetecting] = useState(false);

  const leftCols = (leftDataset?.schema || []).map((c) => c.name);
  const rightCols = (rightDataset?.schema || []).map((c) => c.name);

  useEffect(() => {
    if (!open || !leftDataset || !rightDataset) return;
    setName(`${leftDataset.name} ↔ ${rightDataset.name}`);
    setKeys([{ left_field: "", right_field: "" }]);
    let cancelled = false;
    setDetecting(true);
    (async () => {
      const keyish = (f) => /(^id$|_id$|_?key$|code$|_no$|number$|sku$|email$)/i.test(f || "");
      let pairs = [];
      try {
        const { data } = await api.post("/join-contracts/autodetect", {
          left_dataset_id: leftDataset.id, right_dataset_id: rightDataset.id,
        });
        const all = (data?.suggestions || data?.pairs || [])
          .map((p) => ({ left_field: p.left_field || p.left, right_field: p.right_field || p.right, confidence: p.confidence ?? 0 }))
          .filter((p) => p.left_field && p.right_field)
          .sort((a, b) => b.confidence - a.confidence);
        // Prefill ONE key: a wrong extra key (e.g. revenue = revenue) silently breaks the join.
        const best = all.filter((p) => keyish(p.left_field) || keyish(p.right_field));
        pairs = (best.length ? best : all).slice(0, 1).map(({ left_field, right_field }) => ({ left_field, right_field }));
      } catch { /* fall back to name matching */ }
      if (!pairs.length) {
        const rset = new Set(rightCols);
        const common = leftCols.filter((c) => rset.has(c));
        const idish = common.filter(keyish);
        pairs = (idish.length ? idish : common).slice(0, 1).map((c) => ({ left_field: c, right_field: c }));
      }
      if (!cancelled) {
        if (pairs.length) setKeys(pairs);
        setDetecting(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, leftDataset?.id, rightDataset?.id]);

  const save = async () => {
    const valid = keys.filter((k) => k.left_field && k.right_field);
    if (!valid.length) { toast.error("Pick the matching key field on each side"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/join-contracts", {
        name: (name || `${leftDataset.name} ↔ ${rightDataset.name}`).trim(),
        left_dataset_id: leftDataset.id,
        right_dataset_id: rightDataset.id,
        keys: valid,
        cardinality,
        cross_filter_direction: direction,
        active: true,
      });
      toast.success("Relationship created — joining the field now");
      onOpenChange(false);
      onCreated?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create the relationship");
    } finally { setBusy(false); }
  };

  if (!leftDataset || !rightDataset) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="create-relationship-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Link2 className="w-5 h-5 text-[color:var(--brand-ocean)]" />Link these datasets
          </DialogTitle>
          <DialogDescription>
            <span className="font-medium text-[color:var(--ink-2)]">{leftDataset.name}</span> and{" "}
            <span className="font-medium text-[color:var(--ink-2)]">{rightDataset.name}</span> aren’t related yet.
            Match their key fields once and every chart can mix them from then on.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">Name</Label>
            <Input data-testid="guided-join-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1" />
          </div>
          <div className="rounded-lg border border-[color:var(--glass-border)] p-2.5">
            {detecting ? (
              <div className="flex items-center gap-2 text-[11px] text-[color:var(--ink-3)] py-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />Looking for matching key fields…
              </div>
            ) : (
              <>
                <div className="flex items-center gap-1.5 text-[10px] text-[color:var(--brand-mint)] mb-1.5">
                  <Sparkles className="w-3 h-3" />Suggested key — change it, or add a second one only if the join needs both
                </div>
                <KeyPairsEditor value={keys} onChange={setKeys} leftColumns={leftCols} rightColumns={rightCols}
                  leftLabel={`${leftDataset.name}.field`} rightLabel={`${rightDataset.name}.field`} testIdPrefix="guided-join" />
              </>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Cardinality</Label>
              <Select value={cardinality} onValueChange={setCardinality}>
                <SelectTrigger data-testid="guided-join-cardinality" className="mt-1 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{CARDINALITY.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Filters travel</Label>
              <Select value={direction} onValueChange={setDirection}>
                <SelectTrigger data-testid="guided-join-direction" className="mt-1 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="both">Both directions</SelectItem>
                  <SelectItem value="single">One direction only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="guided-join-save" onClick={save} disabled={busy || detecting}
            className="bg-[color:var(--brand-ocean)] text-white hover:opacity-90">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create & join field"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
